/**
 * Session-scoped persistence for the Insights and Traces filter/view state.
 *
 * Why sessionStorage (and not React state or the URL):
 *  - The two pages live under separate routes, so navigating between them (via
 *    the sidebar) unmounts the page component and would otherwise reset every
 *    filter. Persisting here lets each page "keep the view" when you toggle away
 *    and back — even after visiting an unrelated page in between (#1402).
 *  - It stays a page-local concern (no app-wide provider fetching metadata on
 *    every route) and survives a full refresh, matching how the impersonation
 *    scope is persisted.
 *
 * Each page has its own namespace so the two filter sets are independent: a
 * chart drill-down seeds ONLY the Traces slice (see `seedTracesFilters`), which
 * is what keeps the Insights view untouched when you jump into Traces and back.
 *
 * The state is additionally keyed by the active impersonation domain so a staff
 * user's "view as customer" scopes never share filters. On a scope change the
 * route tree remounts (see `AppRouter`), the hooks re-read under the new key,
 * and the previous scope's entry is simply left dormant.
 */
import type {
  CriteriaFilter,
  DateRangeFilter,
  ScoreFilter,
  TagFilter,
} from "../types";
import type { ScoreMetric } from "./chartData";
import type { DateWindow } from "./dateWindow";
import { getImpersonationDomain } from "../../impersonation/storage";

export const INSIGHTS_NAMESPACE = "insights";
export const TRACES_NAMESPACE = "traces";

/** The full slice we persist per page. View-only fields (groupByKey,
 * scoreMetric) are Insights-only and simply ignored by the Traces page. */
export interface PersistedFilterState {
  filters: TagFilter[];
  criteriaFilter?: CriteriaFilter;
  /**
   * The window as intent. Persisting resolved dates is what made a restored
   * slice go stale overnight (design doc 033), so only this is written.
   */
  dateWindow?: DateWindow;
  scoreFilter?: ScoreFilter;
  groupByKey?: string;
  scoreMetric?: ScoreMetric;
}

/**
 * What may actually come back out of storage: a slice written before
 * `dateWindow` existed still carries resolved dates.
 *
 * Kept off `PersistedFilterState` on purpose. While both fields lived on the one
 * type, passing a resolved `dateRangeFilter` where a window was wanted compiled
 * fine and silently cleared the window instead — the compiler should be the
 * thing that catches that, not a test.
 */
export interface StoredFilterState extends PersistedFilterState {
  /** @deprecated Converted to an absolute window on restore; never written. */
  dateRangeFilter?: DateRangeFilter;
}

/** The filter-only subset a chart drill-down writes into the Traces slice. */
export type DrillDownTarget = Pick<
  PersistedFilterState,
  "filters" | "criteriaFilter" | "dateWindow" | "scoreFilter"
>;

const KEY_PREFIX = "composo:insights-filters";

function storageKey(namespace: string): string {
  const domain = getImpersonationDomain() || "self";
  return `${KEY_PREFIX}:${namespace}:${domain}`;
}

/**
 * Read the persisted slice for a namespace. Returns null when nothing is stored
 * or the value can't be parsed / storage is unavailable (private mode, SSR) so
 * callers fall back to their defaults.
 */
export function readPersistedFilters(
  namespace: string,
): StoredFilterState | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(namespace));
    if (!raw) return null;
    return JSON.parse(raw) as StoredFilterState;
  } catch {
    return null;
  }
}

/** Persist the slice for a namespace; no-ops if storage is unavailable. */
export function writePersistedFilters(
  namespace: string,
  state: PersistedFilterState,
): void {
  try {
    window.sessionStorage.setItem(storageKey(namespace), JSON.stringify(state));
  } catch {
    /* quota / private-mode / SSR — degrade to non-persistent for this session */
  }
}

/**
 * Overwrite the Traces filter slice with a chart drill-down target, then the
 * caller navigates to /traces. `useTracesFilters` lazy-reads this on mount, so
 * the Traces page opens already filtered to the clicked criterion / score band /
 * group / time bucket. Overwrites (rather than merges) so the landing slice is
 * exactly the clicked one, not stacked on top of a previous visit's filters.
 */
export function seedTracesFilters(target: DrillDownTarget): void {
  writePersistedFilters(TRACES_NAMESPACE, {
    filters: target.filters,
    criteriaFilter: target.criteriaFilter,
    dateWindow: target.dateWindow,
    scoreFilter: target.scoreFilter,
  });
}
